<template>
  <div class="grid grid-rows-[auto_auto_1fr] overflow-hidden h-full">
    <ThreadsNav :show-center="false">
      <template #left>
        <div class="flex items-center">
          <i
            class="iconfont icon-left p-2 mr-2 cursor-pointer"
            @click="$router.back()"
          />
          <div v-if="store.saveLoading" class="flex items-center gap-2">
            <VanLoading size="16" />
          </div>
          <div v-else-if="activeTime" class="text-my-gray">
            <i class="iconfont icon-confirm" />
            {{ $t("Texts.saveTime") }}: {{ activeTime }}
          </div>
        </div>
      </template>
      <template #right>
        <button class="btn btn-sm rounded" @click="onPreview">
          {{ $t("Buttons.syncDataAndPreview") }}
        </button>

        <button class="btn btn-primary btn-sm rounded" @click="clickPublish">
          {{ $t("Buttons.publish") }}
        </button>
      </template>
    </ThreadsNav>
    <VanTabs
      v-model:active="store.tabType"
      :line-width="30"
      class="max-w-[100vw]"
    >
      <VanTab :title="$t('game.tab.title')" name="base" />
      <VanTab :title="$t('game.tab.endings')" name="ending" />
      <VanTab :title="$t('game.tab.setting')" name="plot" />
      <VanTab :title="$t('game.show.title')" name="show" />
      <VanTab :title="$t('game.values.title')" name="values" />
      <VanTab
        v-if="store.latestVersion"
        :title="$t('game.updateLog.title')"
        name="log"
      />
    </VanTabs>
    <BaseGalGameEditor v-if="store.tabType === 'base'" />
    <EndingGalGameEditor v-if="store.tabType === 'ending'" />
    <PlotGalGameEditor v-if="store.tabType === 'plot'" />
    <ShowGalGameEditor v-if="store.tabType === 'show'" />
    <UpdateLogGalGameEditor v-if="store.tabType === 'log'" />
    <ValuesGalGameEditor v-if="store.tabType === 'values'" />

    <ModalSelectGalGameMate />
    <ModalPlayerEdit />
    <ModalInput />
    <ModalGeneratingGalGame />
    <ModalPublishGalGame />
    <ModalCloneNode />

    <ModalCharacterDialog />
    <ModalBgmSelection
      v-model:show="store.showBgmModal"
      :selected-tone-id="store.currentBgmId"
      @update:selectedToneId="onSelectBgm"
    />
    <media-select-popup
      v-model:show="store.showImgUploadModal"
      :crop-aspect-ratio="ratio"
      :enable-crop="store.mediaType === 'img'"
      :max-count="1"
      :media-type="store.mediaType"
      :multiple="false"
      :upload-type="store.mediaType === 'img' ? 11 : 9"
      isCropGif
      enable-use-avatar
      replace
      @on-media-selected="onMediaSelected"
    />
    <GalGamePreviewPopup
      :id="store.galGameId"
      ref="previewPopupRef"
      :uuid="store.galGameUUID"
    />
  </div>
</template>

<script lang="ts" setup>
import { useGalGameEditor } from "@/stores/galGameEditor";
import { MediaUploadFileItem } from "@/types/component";
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { showConfirmDialog, showFailToast, showLoadingToast } from "vant";
import { computed } from "vue";
import { useTone } from "@/stores/tone";
import GalGamePreviewPopup, {
  GalGamePreviewPopupInstance,
} from "@/components/mobile/gal-game/editor/GalGamePreviewPopup.vue";
import { watch } from "vue";
import i18n from "@/locales";
import { ApiPostGalGameMedia, ApiPutGalGameMedia } from "@/apis/gal-game";
import ModalSelectGalGameMate from "./components/ModalSelectGalGameMate.vue";

const store = useGalGameEditor();
const route = useRoute();
const toneStore = useTone();

const ratio = computed(() => {
  const { uploadType } = store;
  if (uploadType === 0) {
    return 3 / 4;
  } else if (uploadType === 1) {
    return 16 / 9;
  } else if (uploadType === 2) {
    return 16 / 9;
  }
});

const activeTime = ref("");

const addZero = (num: number) => num.toString().padStart(2, "0");
watch(
  () => store.saveLoading,
  (v) => {
    if (!v) {
      const hour = new Date().getHours();
      const minute = new Date().getMinutes();
      activeTime.value = `${addZero(hour)}:${addZero(minute)}`;
    }
  }
);

const previewPopupRef = ref<null | GalGamePreviewPopupInstance>(null);

const onMediaSelected = async (item: MediaUploadFileItem[]) => {
  const { mediaId, url } = item[0];
  if (store.uploadType === 0) {
    store.tmpMedia = { id: mediaId, url: url! };
  } else if (store.uploadType === 1) {
    store.form.coverMediaId = mediaId;
    store.saveBase();
    store.form.coverMedia = { id: mediaId, url: url! } as Media;
  } else if (store.uploadType === 2) {
    // 上传截图
    const { galGameId, mediaType } = store;
    const loading = showLoadingToast({
      message: i18n.global.t("Message.uploading"),
    });

    if (!store.activeMediaId) {
      const { item } = await ApiPostGalGameMedia({
        galGameId,
        mediaId,
        type: mediaType === "img" ? 1 : 2,
      });

      store.mediaList.push(item.id);
      store.mediaMap[item.id] = {
        ...item.media,
        type: item.type,
        id: item.id,
      };
    } else {
      const { item } = await ApiPutGalGameMedia(store.activeMediaId!, {
        mediaId,
        type: mediaType === "img" ? 1 : 2,
      });
      store.mediaMap[store.activeMediaId!] = {
        ...item.media,
        type: item.type,
        id: item.id,
      };
    }

    loading.close();
  }
};

const onSelectBgm = async (bgmId: number) => {
  if (store.bgmType === 0) {
    store.form.bgmMediaId = useTone().getBgmById(bgmId)!.mediaId;
    store.saveBase();
    store.showBgmModal = false;
  } else if (store.bgmType === 1) {
    store.activeNode.bgmMediaId = useTone().getBgmById(bgmId)!.mediaId;
    store.saveNode();
    store.showBgmModal = false;
  }
};

const onPreview = async () => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.loading"),
  });
  try {
    await previewPopupRef.value?.startPreview();
  } finally {
    loading.close();
  }
};

const clickPublish = async () => {
  if (store.latestVersion) {
    if (store.tabType != "log") return (store.tabType = "log");
    if (!store.activeVersion.description)
      return showFailToast(i18n.global.t("Message.updateLogNotBeenFilledYet"));

    const [latestA, latestB, latestC] = store.latestVersion.version.split(".");
    const [currentA, currentB, currentC] = store.activeVersion.versionNumber;

    if (
      currentA < latestA ||
      (currentA === latestA && currentB < latestB) ||
      (currentA === latestA && currentB === latestB && currentC <= latestC)
    ) {
      return showFailToast(i18n.global.t("Message.updateLogVersionError"));
    }
  }

  try {
    await showConfirmDialog({
      title: i18n.global.t("Titles.releaseGame"),
      message: "",
      confirmButtonText: i18n.global.t("Buttons.momentUpdate"),
      cancelButtonText: i18n.global.t("Buttons.gameOnly"),
      closeOnClickOverlay: true,
    });
    store.showPublishModal = true;
  } catch (e) {
    store.publishGalGame("", false);
  }
};

onMounted(async () => {
  await toneStore.getBgmList();
  let uuid = route.params.uuid as string;
  await store.initGalGame(uuid);
  await store.initNode(store.galGameId);
});
</script>

<style scoped>
/* Your CSS code goes here */
</style>
